/**
 * CypSchoolRepNominationForm.js
 *
 * School-representative nomination for CYP ADHD guardians on the modular
 * dashboard. Mirrors AutismInformantSetupForm's two-step flow (form →
 * completion) but nominates a SENCO / teacher on behalf of the child rather
 * than an informant for the guardian themselves.
 *
 * The guardian's forms are hard-gated until this resolves (nominate or skip
 * with a reason — product decision, 24 Aug). Resolution is recorded on the
 * guardian's own profile document (informants/{uid}.schoolRepNomination):
 *   - nominate → setupNewInformant creates the school rep and the backend
 *     stamps { status: 'nominated', ... } server-side (authoritative);
 *   - skip → this component writes { status: 'skipped', reasonCode, reason }
 *     directly (permitted by the informant self-update rules).
 *
 * How firmly the gate holds depends on the organisation's policy, stamped on
 * the guardian's profile at creation (schoolRepNominationPolicy — informants
 * cannot read the patient's workflowConfig):
 *   - 'optional' (default): nominate, or skip with any reason.
 *   - 'requiredWhenInSchool' (TOC, Sept 2026): a child in school, nursery or
 *     college must have a school representative nominated — the only skip
 *     offered is "not in school". The not-in-school path will later require
 *     an alternative informant (deferred build); until then the clinic
 *     follows up. Copy in this mode is DRAFT pending Tom/Jasmine review.
 */
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { CheckCircleIcon, XIcon, XCircleIcon } from '@heroicons/react/outline';
import { functions, db, auth } from '../../../../firebase';
import { ModularInformantSelfDataContext } from '../../../../context/ModularInformantSelfDataContext';
import { useAuth } from '../../../../hooks/useAuth';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { validateEmail as sharedValidateEmail, validateName } from '../../../../utils/informantValidation';
import ButtonSpinner from '../../../../components/informantManagement/ButtonSpinner';
import WarningPanel from '../../../../components/informantManagement/WarningPanel';

const ERROR_MESSAGE_MAP = {
  'The email address is already in use by another account.': 'This email address is already registered. Please use a different email.',
  'auth/email-already-exists': 'This email address is already registered. Please use a different email.',
  'auth/invalid-email': 'Please enter a valid email address.',
};

function getErrorMessage(error) {
  const raw = error?.message || 'Failed to nominate the school representative. Please try again.';
  return ERROR_MESSAGE_MAP[raw] || raw;
}

const EMPTY_NOMINEE = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  schoolName: '',
  isSubmitting: false,
  isSuccess: false,
  hasError: false,
  errorMessage: '',
};

// Prefabricated skip reasons (product decision: skipping requires a reason).
const SKIP_REASONS = [
  { code: 'notInSchool', label: 'They are not in school at the moment' },
  { code: 'noContact', label: 'I do not know who to contact at the school' },
  { code: 'other', label: 'Another reason' },
];

// The required-when-in-school policy offers exactly one way past the gate:
// the child is not in school. Every other reason means the nomination stands.
const REQUIRED_MODE_SKIP_REASONS = SKIP_REASONS.filter((r) => r.code === 'notInSchool');

export const SCHOOL_REP_NOMINATION_REQUIRED_POLICY = 'requiredWhenInSchool';

// ─── Skip Form Sub-component ─────────────────────────────────────────────────

function SkipForm({ reasonCode, reasonText, onReasonCodeChange, onReasonTextChange, onGoBack, childName, hasError, errorMessage, requiredMode }) {
  const needsText = reasonCode === 'other';
  const skipReasons = requiredMode ? REQUIRED_MODE_SKIP_REASONS : SKIP_REASONS;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
          <XCircleIcon className="w-5 h-5 text-gray-500" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-800 mb-1">
            {requiredMode
              ? `Is ${childName || 'your child'} not in school at the moment?`
              : "Skipping school representative"}
          </h4>
          <p className="text-sm text-gray-600">
            {requiredMode
              ? `If ${childName || 'your child'} is not in school, nursery or college at the moment, confirm below and carry on. Otherwise, please go back and nominate someone from their school — your questionnaires unlock once you have.`
              : `Please tell us why you cannot nominate someone from ${childName ? `${childName}'s` : 'the'} school right now.`}
          </p>
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">Reason for not nominating a school representative</legend>
        {skipReasons.map((option) => (
          <label
            key={option.code}
            className={`flex items-center gap-3 border rounded-md px-3 py-2 text-sm cursor-pointer ${
              reasonCode === option.code ? 'border-violet-500 bg-violet-50' : 'border-gray-300 bg-white'
            }`}
          >
            <input
              type="radio"
              name="skip-reason"
              value={option.code}
              checked={reasonCode === option.code}
              onChange={() => onReasonCodeChange(option.code)}
              className="text-violet-600 focus:ring-violet-500"
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>

      {needsText && (
        <div>
          <label htmlFor="skip-reason-text" className="block text-sm font-medium text-gray-700 mb-1">
            Tell us more
          </label>
          <textarea
            id="skip-reason-text"
            value={reasonText}
            onChange={(e) => onReasonTextChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[80px] resize-none"
            rows={3}
            placeholder="Please explain why you cannot nominate a school representative"
          />
        </div>
      )}

      {hasError && <WarningPanel variant="error">{errorMessage}</WarningPanel>}

      <button
        type="button"
        onClick={onGoBack}
        className="text-sm text-violet-600 hover:text-violet-800 font-medium"
      >
        &larr; Go back to nominate someone
      </button>
    </div>
  );
}

// ─── Nominee Details Form Sub-component ──────────────────────────────────────

// Every field on this form is required before the nomination can be sent.
// The star sits beside the label, not inside it, so the accessible name stays
// "First name". The input's required attribute is what a screen reader announces.
function FieldLabel({ htmlFor, children }) {
  return (
    <div className="flex items-baseline mb-1">
      <label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {children}
      </label>
      <span className="text-red-600 ml-0.5" aria-hidden="true">*</span>
    </div>
  );
}

function NomineeDetailsForm({ nominee, onChange, onToggleSkip, childName, requiredMode }) {
  const inputClass = (hasError) =>
    `w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
      hasError ? 'border-red-300' : 'border-gray-300'
    }`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor="school-rep-first-name">First name</FieldLabel>
          <input
            id="school-rep-first-name"
            type="text"
            required
            placeholder="First name"
            value={nominee.firstName}
            onChange={(e) => onChange('firstName', sanitizeNameInput(e.target.value))}
            className={inputClass(nominee.hasError)}
          />
        </div>
        <div>
          <FieldLabel htmlFor="school-rep-last-name">Last name</FieldLabel>
          <input
            id="school-rep-last-name"
            type="text"
            required
            placeholder="Last name"
            value={nominee.lastName}
            onChange={(e) => onChange('lastName', sanitizeNameInput(e.target.value))}
            className={inputClass(nominee.hasError)}
          />
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="school-rep-role">Their role at the school</FieldLabel>
        <p className="text-xs text-gray-500 mb-1">For example SENCO or class teacher</p>
        <input
          id="school-rep-role"
          type="text"
          required
          placeholder="Role"
          value={nominee.role}
          onChange={(e) => onChange('role', sanitizeNameInput(e.target.value))}
          className={inputClass(nominee.hasError)}
        />
      </div>

      <div>
        <FieldLabel htmlFor="school-rep-school-name">
          {childName ? `${childName}'s school or college` : 'Their school or college'}
        </FieldLabel>
        <input
          id="school-rep-school-name"
          type="text"
          required
          placeholder="Name of the school or college"
          value={nominee.schoolName}
          onChange={(e) => onChange('schoolName', e.target.value)}
          className={inputClass(nominee.hasError)}
        />
      </div>

      <div>
        <FieldLabel htmlFor="school-rep-email">Their email address</FieldLabel>
        <p className="text-xs text-gray-500 mb-1">
          We will email them a link to answer their questionnaires
        </p>
        <input
          id="school-rep-email"
          type="email"
          required
          placeholder="Email address"
          value={nominee.email}
          onChange={(e) => onChange('email', sanitizeEmailInput(e.target.value))}
          className={inputClass(nominee.hasError)}
        />
      </div>

      {nominee.hasError && <WarningPanel variant="error">{nominee.errorMessage}</WarningPanel>}

      <button
        type="button"
        onClick={onToggleSkip}
        className="text-sm text-violet-600 hover:text-violet-800 font-medium underline"
      >
        {requiredMode
          ? `${childName || 'My child'} is not in school, nursery or college`
          : "I'm unable to provide these details"}
      </button>
    </div>
  );
}

// ─── Completion Screen Sub-component ─────────────────────────────────────────

function CompletionScreen({ nominee, wasSkipped, childName, onFinish, requiredMode }) {
  const skippedCopy = requiredMode
    ? `You can now carry on with your own questionnaires. Because ${childName || 'your child'} is not in school, the clinic will be in touch about someone else who knows ${childName || 'them'} well outside your home — like a sports coach, tutor or club leader.`
    : 'You can now carry on with your own questionnaires. The clinic may ask about school information later.';
  return (
    <div className="text-center py-4">
      <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <CheckCircleIcon className="w-7 h-7 text-green-600" aria-hidden="true" />
      </div>
      <h2 className="text-lg font-bold text-gray-900 mb-2">
        {wasSkipped ? 'Thank you for letting us know' : 'School representative nominated'}
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        {wasSkipped
          ? skippedCopy
          : `We have emailed ${nominee.firstName} ${nominee.lastName} a link to answer a few questionnaires about ${childName || 'your child'}. You can now carry on with your own questionnaires.`}
      </p>
      <button
        type="button"
        onClick={onFinish}
        className="px-6 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
      >
        Continue to your questionnaires
      </button>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function CypSchoolRepNominationForm({ isOpen, onClose, startInSkipMode = false }) {
  const {
    header,
    fetchInformantData,
    linkedPatientId,
    patientFirstName,
  } = useContext(ModularInformantSelfDataContext);
  const { claims } = useAuth();

  const [nominee, setNominee] = useState({ ...EMPTY_NOMINEE });
  const [showSkip, setShowSkip] = useState(false);
  const [skipReasonCode, setSkipReasonCode] = useState('');
  const [skipReasonText, setSkipReasonText] = useState('');
  const [skipError, setSkipError] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const [currentStep, setCurrentStep] = useState('form');
  const [wasSkipped, setWasSkipped] = useState(false);

  const mountedRef = useRef(true);
  const actionLockRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setNominee({ ...EMPTY_NOMINEE });
      setShowSkip(startInSkipMode);
      setSkipReasonCode('');
      setSkipReasonText('');
      setSkipError('');
      setCurrentStep('form');
      setWasSkipped(false);
    }
  }, [isOpen, startInSkipMode]);

  const validateNominee = useCallback((n) => {
    const firstErr = validateName(n.firstName, 'First name');
    if (firstErr) return firstErr;
    const lastErr = validateName(n.lastName, 'Last name');
    if (lastErr) return lastErr;
    if (!n.role?.trim()) return 'Their role at the school is required';
    if (!n.schoolName?.trim()) return 'School name is required';
    const emailErr = sharedValidateEmail(n.email);
    if (emailErr) return emailErr;
    return null;
  }, []);

  /**
   * Nominate the school representative via setupNewInformant (guardian
   * caller path). The backend creates the school rep, sends the nomination
   * email, and stamps schoolRepNomination on this guardian's profile.
   */
  const submitNomination = async () => {
    if (actionLockRef.current) return false;
    actionLockRef.current = true;

    const validationError = validateNominee(nominee);
    if (validationError) {
      setNominee((prev) => ({ ...prev, hasError: true, errorMessage: validationError }));
      actionLockRef.current = false;
      return false;
    }

    setNominee((prev) => ({ ...prev, isSubmitting: true, hasError: false, errorMessage: '' }));

    try {
      const setupFn = httpsCallable(functions, 'setupNewInformant');
      await setupFn({
        informantFirstName: nominee.firstName.trim(),
        informantLastName: nominee.lastName.trim(),
        informantEmail: nominee.email.trim().toLowerCase(),
        informantRelationshipToPatient: nominee.role.trim(),
        informantRole: 'schoolRep',
        schoolName: nominee.schoolName.trim(),
        organisation: claims?.organisation || header?.organisation || '',
        assessmentModule: 'cypAdhd',
        patientId: linkedPatientId,
        isPartOfInitialSetup: true,
      });

      if (mountedRef.current) {
        setNominee((prev) => ({ ...prev, isSubmitting: false, isSuccess: true }));
        setWasSkipped(false);
        setCurrentStep('completion');
      }
      // Refresh so the dashboard gate sees the server-side nomination stamp.
      await fetchInformantData?.();
      return true;
    } catch (error) {
      if (mountedRef.current) {
        setNominee((prev) => ({
          ...prev,
          isSubmitting: false,
          hasError: true,
          errorMessage: getErrorMessage(error),
        }));
      }
      return false;
    } finally {
      actionLockRef.current = false;
    }
  };

  /**
   * Record a skip on the guardian's own profile (informants/{uid}), which
   * unlocks their questionnaires. The reason is provider-visible via the
   * manage-informants view.
   */
  const submitSkip = async () => {
    if (!skipReasonCode) {
      setSkipError('Please choose a reason.');
      return;
    }
    if (skipReasonCode === 'other' && !skipReasonText.trim()) {
      setSkipError('Please tell us a little more about why.');
      return;
    }

    const guardianUid = auth.currentUser?.uid;
    if (!guardianUid || isCompleting) return;
    setIsCompleting(true);
    setSkipError('');

    const selected = SKIP_REASONS.find((r) => r.code === skipReasonCode);
    const reason = skipReasonCode === 'other' ? skipReasonText.trim() : selected?.label || '';

    try {
      await updateDoc(doc(db, 'informants', guardianUid), {
        schoolRepNomination: {
          status: 'skipped',
          reasonCode: skipReasonCode,
          reason,
          skippedAt: new Date().toISOString(),
        },
      });
      await fetchInformantData?.();
      if (mountedRef.current) {
        setWasSkipped(true);
        setCurrentStep('completion');
      }
    } catch (err) {
      console.error('Failed to record school-rep nomination skip:', err);
      if (mountedRef.current) setSkipError('Could not save your answer. Please try again.');
    } finally {
      if (mountedRef.current) setIsCompleting(false);
    }
  };

  const handleSubmit = async () => {
    if (showSkip) {
      await submitSkip();
      return;
    }
    await submitNomination();
  };

  const isSubmitting = nominee.isSubmitting || isCompleting;
  const childName = patientFirstName || '';
  // Gate strictness comes from the guardian's own profile stamp (set at
  // creation from the org's cypAdhdFlow.schoolRepNomination).
  const requiredMode = header?.schoolRepNominationPolicy === SCHOOL_REP_NOMINATION_REQUIRED_POLICY;

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open && !isSubmitting) onClose?.(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 radix-dialog-overlay-motion" />
        <AlertDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md bg-white rounded-lg shadow-xl z-50 p-4 md:p-6 max-h-[90vh] overflow-y-auto transition-all duration-300 radix-dialog-content-inset-motion">
          <VisuallyHidden.Root>
            <AlertDialog.Title>
              {currentStep === 'completion' ? 'Nomination complete' : 'Nominate a school representative'}
            </AlertDialog.Title>
          </VisuallyHidden.Root>

          {currentStep === 'form' && (
            <button
              type="button"
              onClick={() => { if (!isSubmitting) onClose?.(); }}
              disabled={isSubmitting}
              className="absolute top-3 right-3 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}

          {currentStep === 'form' ? (
            <>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Nominate a school representative
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {childName
                  ? `Someone from ${childName}'s school — like their SENCO or class teacher — will be asked to answer a few questionnaires about how ${childName} is getting on at school.`
                  : 'Someone from the school — like the SENCO or class teacher — will be asked to answer a few questionnaires about how your child is getting on at school.'}
              </p>

              {showSkip ? (
                <SkipForm
                  reasonCode={skipReasonCode}
                  reasonText={skipReasonText}
                  onReasonCodeChange={(code) => { setSkipReasonCode(code); setSkipError(''); }}
                  onReasonTextChange={(value) => { setSkipReasonText(value); setSkipError(''); }}
                  onGoBack={() => setShowSkip(false)}
                  childName={childName}
                  hasError={Boolean(skipError)}
                  errorMessage={skipError}
                  requiredMode={requiredMode}
                />
              ) : (
                <NomineeDetailsForm
                  nominee={nominee}
                  onChange={(field, value) => setNominee((prev) => ({ ...prev, [field]: value, hasError: false, errorMessage: '' }))}
                  onToggleSkip={() => setShowSkip(true)}
                  childName={childName}
                  requiredMode={requiredMode}
                />
              )}

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting
                    ? <><ButtonSpinner /> Saving...</>
                    : (showSkip ? 'Skip and continue' : 'Nominate school representative')}
                </button>
              </div>
            </>
          ) : (
            <CompletionScreen
              nominee={nominee}
              wasSkipped={wasSkipped}
              childName={childName}
              onFinish={() => onClose?.()}
              requiredMode={requiredMode}
            />
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
